import { Container, Grid } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import BatchEditForm from './components/BatchEditForm';
import { useNotify } from '../../../hooks/useNotify';
import { doSwaggerCall } from '../../../hooks/useSwaggerClient';
import BatchPayoutTable from './components/BatchPayoutTable';
import moment from 'moment';

const initialBatch = {
    name: 'New Batch',
    vendor_id: null,
    from: new Date(),
    to: new Date(),
    batch_no: '12345',
    batch_date: new Date()
};

const BatchEditPage = () => {
    const { batchId } = useParams();
    const { notifyError, notifySuccess } = useNotify();
    const [batch, setBatch] = useState({ initialBatch });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [payouts, setPayouts] = useState([]);
    const [payoutsToBatch, setPayoutsToBatch] = useState([]);

    // Fetch payouts based on current batch parameters
    const fetchPayouts = async batchData => {
        if (!batchData?.vendor_id && !batchData?.from && !batchData?.to) {
            setPayouts([]);
            return;
        }

        try {
            const payoutsData = await doSwaggerCall('bookings', 'getBookingPayouts', {
                startDate: moment(batchData?.from).isValid()
                    ? moment(batchData.from).format('YYYY-MM-DD')
                    : new Date().toISOString().split('T')[0],
                endDate: moment(batchData?.to).isValid()
                    ? moment(batchData.to).format('YYYY-MM-DD')
                    : new Date().toISOString().split('T')[0],
                companyId: batchData?.vendor_id,
                status: 'active',
                account_to: 'vendor',
                pageSize: 10000000,
                batchId
            });
            setPayouts(payoutsData);
        } catch (err) {
            console.error(err);
            notifyError('Error fetching payouts!');
            setPayouts([]);
        }
    };

    // get batch...
    const getBatch = async () => {
        if (batchId && batchId !== 'new') {
            try {
                const data = await doSwaggerCall('batches', 'getBatchById', {
                    batchId: parseInt(batchId, 10)
                });
                setBatch(data);
                // Fetch payouts for this batch
                await fetchPayouts(data);
                // Load existing payouts from batch data
                const existingPayouts = (data.payouts || []).map(payout => ({
                    payout_id: payout.payoutId,
                    pay_amount: payout.payAmount,
                    note: payout.note || ''
                }));
                setPayoutsToBatch(existingPayouts);
                setLoading(false);
            } catch (err) {
                console.error(err);
                notifyError('Error fetching batch details!');
                setLoading(false);
            }
        } else {
            setBatch(initialBatch);
            setLoading(false);
        }
    };

    useEffect(() => {
        getBatch();
    }, [batchId]);

    // Watch for changes in vendor_id, from, or to dates and refetch payouts
    useEffect(() => {
        if (batchId !== 'new' && batch && (batch.vendor_id || batch.from || batch.to)) {
            fetchPayouts(batch);
        }
    }, [batch?.vendor_id, batch?.from, batch?.to]);

    const saveBatch = async () => {
        if (!batch.id) {
            notifyError('Cannot save batch without ID');
            return;
        }
        setSaving(true);
        try {
            const rawUpdateData = {
                vendor_id: batch.vendor_id,
                from: batch.from,
                to: batch.to,
                batch_date: batch.batch_date,
                is_breakage: batch.is_breakage,
                invoice_num: batch.invoice_num,
                invoice_date: batch.invoice_date,
                payment_date: batch.payment_date,
                payment_ref: batch.payment_ref
            };

            // Filter out nulls
            const updateData = Object.fromEntries(
                Object.entries(rawUpdateData).filter(([_, value]) => value !== null)
            );

            // Update batch details
            await doSwaggerCall('batches', 'updateBatch', {
                batchId: batch.id,
                body: { updateData, payoutsToBatch }
            });
            notifySuccess('Batch updated successfully!');
            getBatch();
        } catch (err) {
            console.error(err);
            notifyError('Error updating batch!');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Grid container justifyContent="center">
                <h1>Loading...</h1>
            </Grid>
        );
    }

    return (
        <Container className="activity-edit page" maxWidth="no">
            <Grid className="pageWithSidebar">
                <BatchEditForm batch={batch} setBatch={setBatch} />
                <Grid className="pageContent">
                    <BatchPayoutTable
                        batch={batch}
                        payouts={payouts?.bookingPayouts || []}
                        payoutsToBatch={payoutsToBatch || []}
                        setPayoutsToBatch={setPayoutsToBatch}
                        onSave={saveBatch}
                    />
                </Grid>
            </Grid>
        </Container>
    );
};

export default BatchEditPage;
